import { sendStripeInvoice, paymentQrDataUrl, formatStripeAmount } from '../services/stripeInvoice.js';
import { sendInvoiceEmail } from '../services/invoiceEmail.js';
import { generateInvoicePdf } from '../services/invoicePdf.js';
import { parseLocalDate } from '../services/dates.js';
import { RevenueService, RevenueMixin } from '../services/revenue.js';

export const Invoices = {
  name: 'InvoiceManager',
  inject: ['user', 'isAuthenticated'],
  mixins: [RevenueMixin], // Add consistent revenue handling
  data() {
    return {
      invoices: [],
      jobs: [],
      loading: true,
      error: null,
      stripeBusyId: null,
      stripePayment: null,
      stripeQr: null,
      filterStatus: 'all',
      searchTerm: '',
      sortBy: 'dateCreated',
      sortOrder: 'desc',
      selectedInvoice: null,
      showInvoiceModal: false,
      isGeneratingPDF: false,
      isSending: false,
      dateRange: {
        start: '',
        end: ''
      },
      // Performance tracking
      lastFetchTime: null,
      invoiceCache: new Map(),
      // Pagination for performance
      currentPage: 1,
      itemsPerPage: 25,
      // Debounced search
      searchTimeout: null
    };
  },
  computed: {
    // Memoized filtered invoices with pagination
    filteredInvoices() {
      let filtered = [...this.invoices];
      
      // Use Set to track unique invoice IDs and prevent duplicates
      const uniqueInvoices = new Map();
      
      filtered.forEach(invoice => {
        const uniqueKey = `${invoice.jobId}-${invoice.id}`;
        if (!uniqueInvoices.has(uniqueKey)) {
          uniqueInvoices.set(uniqueKey, invoice);
        } else {
          // Keep the most recent version if duplicates exist
          const existing = uniqueInvoices.get(uniqueKey);
          if (new Date(invoice.dateCreated) > new Date(existing.dateCreated)) {
            uniqueInvoices.set(uniqueKey, invoice);
          }
        }
      });
      
      filtered = Array.from(uniqueInvoices.values());

      // Filter by status
      if (this.filterStatus !== 'all') {
        filtered = filtered.filter(invoice => invoice.status === this.filterStatus);
      }

      // Filter by search term (debounced)
      if (this.searchTerm) {
        const term = this.searchTerm.toLowerCase();
        filtered = filtered.filter(invoice => 
          invoice.customerName?.toLowerCase().includes(term) ||
          invoice.invoiceNumber?.toLowerCase().includes(term) ||
          invoice.job?.serviceType?.toLowerCase().includes(term) ||
          invoice.customerEmail?.toLowerCase().includes(term)
        );
      }

      // Filter by date range.
      // Invoices with no dateCreated coerce to new Date(null), the epoch, so a
      // start bound excludes them. Deliberate: an invoice with no date cannot
      // satisfy a date range, and deriving one from the job would put a
      // fabricated date on a financial record. The admin migration backfills them.
      if (this.dateRange.start) {
        filtered = filtered.filter(invoice => 
          new Date(invoice.dateCreated) >= new Date(this.dateRange.start)
        );
      }
      if (this.dateRange.end) {
        filtered = filtered.filter(invoice => 
          new Date(invoice.dateCreated) <= new Date(this.dateRange.end)
        );
      }

      // Sort efficiently
      filtered.sort((a, b) => {
        let aVal = a[this.sortBy];
        let bVal = b[this.sortBy];

        if (this.sortBy.includes('date') || this.sortBy.includes('amount') || this.sortBy.includes('total')) {
          aVal = this.sortBy.includes('date') ? new Date(aVal || 0) : parseFloat(aVal || 0);
          bVal = this.sortBy.includes('date') ? new Date(bVal || 0) : parseFloat(bVal || 0);
        } else {
          aVal = (aVal || '').toString().toLowerCase();
          bVal = (bVal || '').toString().toLowerCase();
        }

        return this.sortOrder === 'asc' ? (aVal > bVal ? 1 : -1) : (aVal < bVal ? 1 : -1);
      });

      return filtered;
    },
    
    // Paginated invoices for better performance
    paginatedInvoices() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.filteredInvoices.slice(start, end);
    },
    
    totalPages() {
      return Math.ceil(this.filteredInvoices.length / this.itemsPerPage);
    },
    
    // Use consistent revenue stats from mixin
    invoiceStats() {
      return {
        // Total revenue calculation - only PAID invoices count as actual revenue
        totalRevenue: this.revenueStats.actualRevenue,

        // Pending amounts - invoices sent but not paid
        pendingAmount: this.revenueStats.pendingRevenue,

        // Paid amount - same as total revenue for consistency
        paidAmount: this.revenueStats.actualRevenue,

        // Counts
        totalCount: this.revenueStats.totalInvoices,
        pendingCount: this.revenueStats.pendingInvoices,
        paidCount: this.revenueStats.paidInvoices,
        sentCount: this.revenueStats.sentInvoices,

        // Outstanding (all unpaid invoices)
        outstandingAmount: this.revenueStats.outstandingRevenue,

        // Conversion metrics
        conversionRate: this.revenueStats.conversionRate || 0
      };
    }
  },
  
  methods: {
    // Enhanced fetchInvoices with duplicate prevention based on actual database structure
    // Enhanced fetchInvoices with resend logic - treats multiple invoices per job as resends
async fetchInvoices() {
  console.log('📋 Starting fetchInvoices method...');
  this.loading = true;
  this.error = null;
  
  try {
    if (!this.user?.uid) {
      this.loading = false;
      return;
    }

    console.log('🔄 Fetching invoices for user:', this.user.uid);

    // Deliberately scoped to the signed-in user, admin or not: this page is
    // "my invoices" for everyone. Cross-account visibility lives on /admin,
    // which already reads every job. An earlier version widened this query for
    // admins, which was wrong - it put another operator's invoices in the
    // owner's own list.
    const jobsSnapshot = await firebase
      .database()
      .ref('jobs')
      .orderByChild('userId')
      .equalTo(this.user.uid)
      .once('value');

    const jobs = [];
    const invoicesMap = new Map(); // Use Map to prevent duplicates
    const processedJobs = new Set(); // Track processed jobs

    if (jobsSnapshot.exists()) {
      jobsSnapshot.forEach((childSnapshot) => {
        const job = childSnapshot.val();
        job.id = childSnapshot.key;
        jobs.push(job);

        // Skip if we've already processed this job
        if (processedJobs.has(job.id)) {
          console.warn('🚨 Duplicate job detected and skipped:', job.id);
          return;
        }
        processedJobs.add(job.id);

        // Process existing invoices from this job with improved status handling
        if (job.invoices && typeof job.invoices === 'object') {
          const jobInvoices = Object.entries(job.invoices);

          if (jobInvoices.length > 0) {
            // Find the invoice with the most advanced status (paid > overdue > sent > pending)
            const statusPriority = {
              'paid': 4,
              'overdue': 3,
              'sent': 2,
              'pending': 1
            };

            // Sort by status priority first, then by date
            const sortedInvoices = jobInvoices
              // Do NOT drop undated invoices: they still belong on the books, and
              // the stat cards count them, so filtering here made the table
              // disagree with the totals directly above it. They render with a
              // blank date until the admin migration backfills them.
              .sort((a, b) => {
                const statusA = statusPriority[a[1].status] || 0;
                const statusB = statusPriority[b[1].status] || 0;

                // If same status, sort by date (newest first)
                if (statusA === statusB) {
                  const dateA = new Date(a[1].sentDate || a[1].dateCreated || 0);
                  const dateB = new Date(b[1].sentDate || b[1].dateCreated || 0);
                  return dateB - dateA;
                }

                // Otherwise, sort by status priority (highest first)
                return statusB - statusA;
              });

            if (sortedInvoices.length > 0) {
              const [latestInvoiceId, latestInvoiceData] = sortedInvoices[0];
              const uniqueKey = job.id; // Use job ID as unique key since one invoice per job

              // Use consistent status from RevenueService
              const rawStatus = latestInvoiceData.status;
              console.log(`🔍 Raw status for job ${job.id}:`, rawStatus);

              const normalizedStatus = (rawStatus || 'sent').toLowerCase();
              const validStatus = Object.values(RevenueService.INVOICE_STATUSES).includes(normalizedStatus)
                ? normalizedStatus
                : RevenueService.INVOICE_STATUSES.SENT;

              console.log(`🔍 Job ${job.id}: raw="${rawStatus}" -> normalized="${normalizedStatus}" -> valid="${validStatus}"`);

              // Check if invoice is overdue (pending/sent > 7 days ago without payment)
              const sentDate = new Date(latestInvoiceData.sentDate || latestInvoiceData.dateCreated);
              const daysSinceSent = (Date.now() - sentDate.getTime()) / (1000 * 60 * 60 * 24);
              const isOverdue = (validStatus === RevenueService.INVOICE_STATUSES.SENT ||
                                validStatus === RevenueService.INVOICE_STATUSES.PENDING) &&
                                daysSinceSent > 7;

              const finalStatus = isOverdue ? RevenueService.INVOICE_STATUSES.OVERDUE : validStatus;
              console.log(`🔍 Final status for job ${job.id}: "${finalStatus}" (overdue: ${isOverdue})`);

              const invoice = {
                id: latestInvoiceId,
                jobId: job.id,
                job: job,
                customerName: job.customerName || 'Unknown Customer',
                customerEmail: job.customerEmail || '',
                serviceType: job.serviceType || 'Service',
                amount: this.parseAmount(latestInvoiceData.amount || latestInvoiceData.total || job.quotedPrice),
                total: this.parseAmount(latestInvoiceData.amount || latestInvoiceData.total || job.quotedPrice),
                status: finalStatus,
                dateCreated: latestInvoiceData.dateCreated || RevenueService.toLocalDateString(latestInvoiceData.sentDate),
                dateSent: RevenueService.toLocalDateString(latestInvoiceData.sentDate),
                datePaid: RevenueService.toLocalDateString(latestInvoiceData.datePaid),
                invoiceNumber: latestInvoiceData.invoiceNumber || RevenueService.generateInvoiceNumber(job.id, latestInvoiceId),
                serviceLocation: job.serviceLocation || '',
                sentBy: latestInvoiceData.sentBy || '',
                timestamp: latestInvoiceData.sentDate || latestInvoiceData.dateCreated || Date.now(),
                resendCount: sortedInvoices.length - 1, // Number of resends (0 = original send)
                totalSends: sortedInvoices.length, // Total times sent
                allInvoices: sortedInvoices, // Keep full history for reference
                originalSentDate: sortedInvoices[sortedInvoices.length - 1][1].sentDate, // First send date
                daysSinceSent: Math.floor(daysSinceSent),
                isOverdue: isOverdue
              };

              invoicesMap.set(uniqueKey, invoice);

              if (invoice.resendCount > 0) {
                console.log(`📧 Invoice for job ${job.id} was resent ${invoice.resendCount} times`);
              }
              if (isOverdue) {
                console.log(`⚠️ Invoice for job ${job.id} is overdue (${Math.floor(daysSinceSent)} days)`);
              }
            }
          }
        } 
        // Check for completed jobs that don't have any invoices yet (these need invoices created)
        else if (job.status === 'completed') {
          const uniqueKey = job.id;

          const pendingInvoice = {
            id: 'pending',
            jobId: job.id,
            job: job,
            customerName: job.customerName || 'Unknown Customer',
            customerEmail: job.customerEmail || '',
            serviceType: job.serviceType || 'Service',
            amount: this.parseAmount(job.quotedPrice),
            total: this.parseAmount(job.quotedPrice),
            status: RevenueService.INVOICE_STATUSES.PENDING, // Use consistent status
            dateCreated: job.dateSubmitted || new Date().toISOString().split('T')[0],
            dateSent: null,
            datePaid: null,
            invoiceNumber: RevenueService.generateInvoiceNumber(job.id, 'pending'),
            serviceLocation: job.serviceLocation || '',
            sentBy: '',
            timestamp: job.dateSubmitted ? new Date(job.dateSubmitted).getTime() : Date.now(),
            resendCount: 0,
            totalSends: 0,
            allInvoices: [],
            originalSentDate: null,
            daysSinceSent: null,
            isOverdue: false
          };

          invoicesMap.set(uniqueKey, pendingInvoice);
          console.log('📋 Added pending invoice for completed job without invoices:', job.id);
        }
      });
    }

    // Convert Map to Array (automatically deduplicates by job)
    const uniqueInvoices = Array.from(invoicesMap.values());

    // Count different types for logging and debugging
    const sentInvoices = uniqueInvoices.filter(inv => inv.status === RevenueService.INVOICE_STATUSES.SENT);
    const paidInvoices = uniqueInvoices.filter(inv => inv.status === RevenueService.INVOICE_STATUSES.PAID);
    const pendingInvoices = uniqueInvoices.filter(inv => inv.status === RevenueService.INVOICE_STATUSES.PENDING);
    const overdueInvoices = uniqueInvoices.filter(inv => inv.status === RevenueService.INVOICE_STATUSES.OVERDUE);
    const resentInvoices = uniqueInvoices.filter(inv => inv.resendCount > 0);

    console.log(`✅ Processed ${jobs.length} jobs, found ${uniqueInvoices.length} unique invoices`);
    console.log(`📊 Breakdown: ${sentInvoices.length} sent, ${paidInvoices.length} paid, ${pendingInvoices.length} pending, ${overdueInvoices.length} overdue`);
    console.log(`🔄 ${resentInvoices.length} invoices have been resent`);

    // Debug: Log all invoice statuses to identify any issues
    console.log('🔍 Debug - All invoice statuses:', uniqueInvoices.map(inv => `${inv.customerName}: ${inv.status}`));
    
    this.jobs = jobs;
    this.invoices = uniqueInvoices;
    this.lastFetchTime = Date.now();

    // Clear cache when data changes
    this.invoiceCache.clear();

    // Clear revenue cache when new data is loaded
    this.clearRevenueCache();

  } catch (error) {
    console.error('❌ Error fetching invoices:', error);
    this.error = 'Failed to load invoices. Please try again.';
  } finally {
    this.loading = false;
  }
},
    
    // Helper method to safely parse amounts
    parseAmount(priceString) {
      if (!priceString) return 0;
      const cleaned = priceString.toString().replace(/[$,]/g, '');
      const parsed = parseFloat(cleaned);
      return isNaN(parsed) ? 0 : parsed;
    },
    
    // Use consistent invoice number generation from RevenueService
    generateInvoiceNumber(jobId, invoiceId) {
      return RevenueService.generateInvoiceNumber(jobId, invoiceId);
    },
    
    // Debounced search for better performance
    onSearchChange(event) {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.searchTerm = event.target.value;
        this.currentPage = 1; // Reset to first page on search
      }, 300);
    },
    
    // Performance optimized PDF generation
    // Delegates to the shared generator; see src/services/invoicePdf.js.
    async generatePDF(invoice) {
      if (this.isGeneratingPDF) return null;
      this.isGeneratingPDF = true;
      try {
        const { doc } = await generateInvoicePdf({
          job: invoice.job || {},
          invoice,
          uid: this.user && this.user.uid,
        });
        return doc;
      } catch (error) {
        console.error('Error generating PDF:', error);
        this.error = 'Failed to generate PDF';
        return null;
      } finally {
        this.isGeneratingPDF = false;
      }
    },

    async downloadPDF(invoice) {
      try {
        const doc = await this.generatePDF(invoice);
        if (doc) {
          doc.save(`${invoice.invoiceNumber}.pdf`);
        }
      } catch (error) {
        console.error('❌ Error downloading PDF:', error);
        alert('Error generating PDF. Please try again.');
      }
    },

    // Raise the invoice in Stripe. Stripe emails it and hosts the payment page,
    // which is what actually fixed deliverability - our own SMTP was being
    // quarantined silently by recipient providers.
    async requestStripePayment(invoice) {
      if (this.stripeBusyId) return;

      // A Stripe invoice cannot be deleted once finalised - only voided - and the
      // customer is emailed immediately. Confirm who is about to be billed and
      // for how much, because there is no undo.
      const who = invoice.customerEmail || invoice.customerName || 'this customer';
      const amount = this.formatCurrency(invoice.amount || invoice.total);
      if (!window.confirm(
        `Send a payable Stripe invoice to ${who} for ${amount}?\n\n` +
        `Stripe emails it immediately. A sent invoice can be voided but not deleted.`
      )) {
        return;
      }

      this.stripeBusyId = invoice.id;
      this.stripeQr = null;
      try {
        const result = await sendStripeInvoice({
          jobId: invoice.jobId,
          invoiceId: invoice.id,
        });

        this.stripePayment = {
          number: result.number,
          hostedInvoiceUrl: result.hostedInvoiceUrl,
          amountLabel: formatStripeAmount(result.amountDue),
          livemode: result.livemode,
        };

        // The QR is how payment works on site: the customer scans it off this
        // screen and pays on their own phone, where their wallet lives.
        try {
          this.stripeQr = await paymentQrDataUrl(result.hostedInvoiceUrl);
        } catch (error) {
          console.error('Could not render the payment QR:', error);
        }

        await this.fetchInvoices();
      } catch (error) {
        console.error('Stripe invoice failed:', error);
        alert(`Could not send this invoice through Stripe.\n\n${error.message || error}`);
      } finally {
        this.stripeBusyId = null;
      }
    },

    async sendInvoice(invoice) {
      if (this.isSending) return;
      
      this.isSending = true;
      try {
        console.log('📧 Sending invoice:', invoice.invoiceNumber);

        // Actually email it. This used to write the status and stop, so the
        // dashboard recorded invoices as sent that no customer ever received.
        // The status is only written below, after the send resolves.
        await sendInvoiceEmail({
          job: invoice.job || {},
          invoice,
          uid: this.user && this.user.uid,
        });

        // Through the server, like every other invoice write. A direct write
        // here would be refused once the rules deny client writes to invoices.
        await RevenueService.updateInvoiceStatus(invoice.jobId, invoice.id, 'sent');
        
        // Update local data efficiently
        const invoiceIndex = this.invoices.findIndex(inv => 
          inv.id === invoice.id && inv.jobId === invoice.jobId
        );
        
        if (invoiceIndex !== -1) {
          this.invoices[invoiceIndex] = {
            ...this.invoices[invoiceIndex],
            status: 'sent',
            // Local calendar date, not the UTC one: toISOString() after 8pm in
            // Orlando records tomorrow.
            dateSent: RevenueService.toLocalDateString(Date.now())
          };
        }
        
        // Clear cache
        this.invoiceCache.clear();
        
        alert('✅ Invoice sent successfully!');
      } catch (error) {
        console.error('❌ Error sending invoice:', error);
        // Show the real reason. A generic "please try again" hid an SMTP
        // authentication failure that had been failing every send.
        alert(`Invoice NOT sent.\n\n${error.message || error}`);
      } finally {
        this.isSending = false;
      }
    },

    // Enhanced markAsPaid with better state management
    async markAsPaid(invoice) {
      // Prevent double-clicking
      if (this.isUpdatingInvoice) {
        console.log('⏳ Update already in progress, please wait...');
        return;
      }

      if (!confirm(`Mark invoice ${invoice.invoiceNumber} as paid?`)) {
        return;
      }

      console.log(`💰 Marking invoice ${invoice.invoiceNumber} as paid...`);

      try {
        const success = await this.updateInvoiceStatus(
          invoice.jobId,
          invoice.id,
          RevenueService.INVOICE_STATUSES.PAID,
          {
            datePaid: new Date().toISOString().split('T')[0],
            paidAt: Date.now()
          }
        );

        if (success) {
          console.log(`✅ Invoice ${invoice.invoiceNumber} successfully marked as paid`);
          // The updateInvoiceStatus method now handles all refreshing
        } else {
          alert('❌ Failed to update invoice status.');
        }
      } catch (error) {
        console.error('❌ Error marking invoice as paid:', error);
        alert('❌ Failed to update invoice status.');
      }
    },

    // Enhanced markAsOverdue with better state management
    async markAsOverdue(invoice) {
      if (this.isUpdatingInvoice) {
        console.log('⏳ Update already in progress, please wait...');
        return;
      }

      if (!confirm(`Mark invoice ${invoice.invoiceNumber} as overdue?`)) {
        return;
      }

      console.log(`⚠️ Marking invoice ${invoice.invoiceNumber} as overdue...`);

      try {
        const success = await this.updateInvoiceStatus(
          invoice.jobId,
          invoice.id,
          RevenueService.INVOICE_STATUSES.OVERDUE,
          { markedOverdueAt: Date.now() }
        );

        if (success) {
          console.log(`⚠️ Invoice ${invoice.invoiceNumber} marked as overdue`);
        } else {
          alert('❌ Failed to update invoice status.');
        }
      } catch (error) {
        console.error('❌ Error marking invoice as overdue:', error);
        alert('❌ Failed to update invoice status.');
      }
    },

    // Enhanced cancelInvoice with better state management
    async cancelInvoice(invoice) {
      if (this.isUpdatingInvoice) {
        console.log('⏳ Update already in progress, please wait...');
        return;
      }

      if (!confirm(`Cancel invoice ${invoice.invoiceNumber}? This action cannot be undone.`)) {
        return;
      }

      console.log(`🚫 Cancelling invoice ${invoice.invoiceNumber}...`);

      try {
        const success = await this.updateInvoiceStatus(
          invoice.jobId,
          invoice.id,
          RevenueService.INVOICE_STATUSES.CANCELLED,
          {
            cancelledAt: Date.now(),
            cancelledReason: 'Manual cancellation'
          }
        );

        if (success) {
          console.log(`🚫 Invoice ${invoice.invoiceNumber} cancelled`);
        } else {
          alert('❌ Failed to cancel invoice.');
        }
      } catch (error) {
        console.error('❌ Error cancelling invoice:', error);
        alert('❌ Failed to cancel invoice.');
      }
    },

    // Refresh invoice data
    async refreshData() {
      this.invoiceCache.clear();
      this.clearRevenueCache();
      await this.fetchInvoices();
    },

    // Pagination methods
    goToPage(page) {
      this.currentPage = Math.max(1, Math.min(page, this.totalPages));
    },
    
    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
      }
    },
    
    prevPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
      }
    },

    formatCurrency(amount) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD'
      }).format(amount || 0);
    },

    formatDate(dateStr) {
      const date = parseLocalDate(dateStr);
      return date ? date.toLocaleDateString('en-US') : 'N/A';
    },

    // Updated to use consistent status badge method from revenue service
    getStatusBadgeClass(status) {
      return this.getInvoiceStatusBadge(status);
    },

    setSortBy(field) {
      if (this.sortBy === field) {
        this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortBy = field;
        this.sortOrder = 'desc';
      }
      this.currentPage = 1; // Reset to first page on sort
    },

    viewInvoice(invoice) {
      this.selectedInvoice = invoice;
      this.showInvoiceModal = true;
    },

    closeInvoiceModal() {
      this.showInvoiceModal = false;
      this.selectedInvoice = null;
    },
    
    // Manual refresh method
    async refreshData() {
      this.invoiceCache.clear();
      await this.fetchInvoices();
    }
  },

  async mounted() {
    console.log('🚀 Invoice Manager mounted');
    await this.fetchInvoices();
  },
  
  // Cleanup on unmount
  beforeUnmount() {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
    this.invoiceCache.clear();
  },

 template: '#invoices'
};
